import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAdmin } from '../context/AdminContext';
import { AnalyticsChart } from './AnalyticsChart';
import { getAvatarGradient } from '../utils/avatarColor';
import { AcquisitionTab, InstalledViaCard, touchLabel } from './AcquisitionPanel';
import {
  ArrowLeft, Server, Users, Hash, Layers, FileText,
  Bot, MessageSquare, Zap, ChevronRight, Settings, Ban,
  CheckCircle, BarChart2, Clock, Shield, Loader2, Compass
} from 'lucide-react';

// This user's role in a server (API field servers[].role).
const ROLE_STYLE = {
  owner: { label: 'Owner', long: 'the owner', title: 'Owns this server', className: 'border-emerald-500/20 text-emerald-300 bg-emerald-500/5' },
  installer: { label: 'Installer', long: 'the installer (not the owner)', title: 'Added the bot to this server', className: 'border-sky-500/20 text-sky-300 bg-sky-500/5' },
  member: { label: 'Member', long: 'a member', title: 'Uses translation in this server', className: 'border-slate-500/20 text-slate-300 bg-slate-500/5' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const {
    API_BASE,
    apiFetch,
    resetCharacters,
    addCredits,
    changePlan,
    changeServerPlan,
    toggleStatus,
    toggleServerStatus,
    rebootServer,
    processPendingCharacters,
    can,
  } = useAdmin();

  // The table row no longer carries full server/usage details - the manage page fetches
  // its own data from GET /api/admin/users/:userId.
  const userQuery = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/users/${userId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res) throw new Error('Session expired');
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Failed to fetch user');
      return res.json();
    },
    enabled: !!userId,
  });

  const selectedUser = userQuery.data || null;
  const [selectedServerId, setSelectedServerId] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [isEditingServerPlan, setIsEditingServerPlan] = useState(false);
  const [selectedServerPlan, setSelectedServerPlan] = useState('free');

  useEffect(() => {
    if (selectedUser?.servers?.length > 0) {
      setSelectedServerId((prev) => (selectedUser.servers.some(s => s.id === prev) ? prev : selectedUser.servers[0].id));
    } else {
      setSelectedServerId(null);
    }
  }, [userId, selectedUser]);

  useEffect(() => {
    setIsEditingServerPlan(false);
  }, [selectedServerId]);

  const selectedServer = selectedUser?.servers?.find(s => s.id === selectedServerId) || null;

  const handleServerSelect = (serverId) => {
    setSelectedServerId(serverId);
    setActiveTab('server');
  };

  if (userQuery.isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mb-3" />
        <span className="text-xs text-slate-500">Loading developer profile...</span>
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <Server className="w-12 h-12 text-slate-500 animate-bounce mb-3" />
        <h2 className="text-xl font-bold text-white">User not found</h2>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-xl text-xs transition-all"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const SERVER_GRADIENTS = [
    'from-indigo-500 to-purple-600',
    'from-pink-500 to-rose-600',
    'from-emerald-400 to-teal-500',
    'from-amber-400 to-orange-500',
    'from-cyan-400 to-blue-500',
  ];

  // DM-only quota - separate from any server's character usage
  const usageUsed = selectedUser.personalUsage?.used || 0;
  const usageLimit = selectedUser.personalUsage?.limit || 1;
  const usagePct = Math.min(100, (usageUsed / usageLimit) * 100);
  const serverUsagePct = selectedServer
    ? Math.min(100, ((selectedServer.charactersUsed || 0) / (selectedServer.maxCharactersPerDay || 1)) * 100)
    : 0;
  const usageBarColor = usagePct > 85
    ? 'from-rose-500 to-red-400 shadow-[0_0_8px_rgba(239,68,68,0.4)]'
    : usagePct > 60
      ? 'from-amber-500 to-orange-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]'
      : 'from-indigo-500 to-purple-500 shadow-[0_0_8px_rgba(99,102,241,0.4)]';

  return (
    <div className="min-h-screen bg-[#070a13] text-slate-200 flex flex-col font-sans">

      {/* Breadcrumb Header */}
      <header className="sticky top-0 z-40 bg-[#070a13]/80 backdrop-blur-md border-b border-white/5 px-6 py-3.5 flex items-center gap-2.5">
        <button
          onClick={() => navigate('/')}
          className="group flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900/60 hover:bg-indigo-500/10 border border-white/5 hover:border-indigo-500/30 text-xs font-bold text-slate-400 hover:text-indigo-200 transition-all"
        >
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          Dashboard
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
        <span className="text-xs font-bold text-slate-300">{selectedUser.name}</span>
        {activeTab === 'server' && selectedServer && (
          <>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
            <span className="text-xs font-bold text-indigo-300">{selectedServer.name}</span>
          </>
        )}
        {activeTab === 'personal' && (
          <>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
            <span className="text-xs font-bold text-indigo-300">Personal Usage</span>
          </>
        )}
        {activeTab === 'acquisition' && (
          <>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
            <span className="text-xs font-bold text-indigo-300">Acquisition</span>
          </>
        )}
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

        {/* ─── LEFT COLUMN ─── */}
        <div className="lg:col-span-4 space-y-5">

          {/* User Profile */}
          <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-full bg-gradient-to-tr ${getAvatarGradient(selectedUser.id)} flex items-center justify-center font-extrabold text-white text-sm shadow-inner shrink-0`}>
                {selectedUser.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-black text-white truncate">{selectedUser.name}</h2>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border inline-block mt-0.5 ${selectedUser.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                  {selectedUser.status}
                </span>
              </div>

            </div>

            <div className="space-y-2.5 text-xs border-t border-white/5 pt-4">
              {[
                {
                  label: 'Email',
                  value: selectedUser.email
                    ? `${selectedUser.email}${selectedUser.emailVerified ? '' : ' (unverified)'}`
                    : '—',
                },
                { label: 'Joined', value: formatDate(selectedUser.joined) },
                { label: 'Came from', value: touchLabel(selectedUser.acquisition?.firstTouch) || '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between gap-4">
                  <span className="text-slate-500 shrink-0">{label}</span>
                  <span className="text-slate-300 text-right truncate">{value}</span>
                </div>
              ))}

            </div>
          </div>


          {/* Server List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <Bot className="w-3.5 h-3.5 text-indigo-400" />
                Servers ({selectedUser.servers?.length || 0})
              </h3>
              <span className="text-[9px] text-slate-600 font-medium">Click to view →</span>
            </div>

            <div className="space-y-2">
              {selectedUser.servers?.map((srv, idx) => {
                const isActive = selectedServerId === srv.id && activeTab === 'server';
                const avatarGradient = SERVER_GRADIENTS[idx % SERVER_GRADIENTS.length];
                const initials = srv.name.split(' ').map(n => n[0]).join('').substring(0, 3);

                return (
                  <div
                    key={srv.id}
                    onClick={() => handleServerSelect(srv.id)}
                    className={`p-3.5 rounded-xl cursor-pointer transition-all duration-150 border ${isActive
                      ? 'bg-indigo-500/10 border-indigo-500/40 ring-1 ring-indigo-500/20'
                      : 'bg-slate-900/40 border-white/5 hover:border-white/10 hover:bg-slate-900/60'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full bg-gradient-to-tr ${avatarGradient} flex items-center justify-center font-extrabold text-white text-[10px] shadow-inner shrink-0 relative`}>
                        {initials}
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#070a13] ${srv.status === 'Online' ? 'bg-emerald-400' : srv.status === 'Maintenance' ? 'bg-amber-400' : 'bg-slate-500'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">{srv.name}</span>
                          {isActive && (
                            <span className="text-[9px] text-indigo-300 font-bold bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.5 rounded shrink-0">
                              Viewing
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[10px] text-slate-500 flex items-center gap-1">
                            <Users className="w-2.5 h-2.5" />
                            {(srv.members || 0).toLocaleString()}
                          </span>
                          <span className={`text-[10px] font-semibold ${srv.status === 'Online' ? 'text-emerald-400' : srv.status === 'Maintenance' ? 'text-amber-400' : 'text-slate-500'}`}>
                            {srv.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 text-[9px] font-bold">
                          <span className={`px-1.5 py-0.5 rounded-md uppercase tracking-wider ${srv.plan === 'pro'
                            ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20'
                            : srv.plan === 'premium'
                              ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                              : 'bg-slate-500/10 text-slate-400 border border-white/5'
                            }`}>
                            {srv.plan ? srv.plan : 'free'}
                          </span>
                          {srv.role && (
                            <span className={`px-1.5 py-0.5 rounded-md border ${ROLE_STYLE[srv.role]?.className || ROLE_STYLE.member.className}`} title={ROLE_STYLE[srv.role]?.title}>
                              {ROLE_STYLE[srv.role]?.label || srv.role}
                            </span>
                          )}
                          {srv.paidByThisUser && (
                            <span className="px-1.5 py-0.5 rounded-md border border-lime-500/20 text-lime-300 bg-lime-500/5" title="This user bought the subscription">
                              Paid for it
                            </span>
                          )}
                          <span className="text-slate-500 font-mono">
                            Spent: ${(typeof srv.subscriptionSpent === 'number' ? srv.subscriptionSpent : (srv.subscriptionCost || 0)).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-colors ${isActive ? 'text-indigo-400' : 'text-slate-600'}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* ─── RIGHT COLUMN ─── */}
        <div className="lg:col-span-8 space-y-5">

          {/* Tab Switcher */}
          <div className="flex gap-2 p-1 bg-slate-900/50 border border-white/5 rounded-xl w-fit">
            <button
              onClick={() => setActiveTab('personal')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'personal'
                ? 'bg-indigo-500/20 text-indigo-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-300'
                }`}
            >
              <BarChart2 className="w-3.5 h-3.5" />
              Personal Usage
            </button>
            <button
              onClick={() => setActiveTab('server')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'server'
                ? 'bg-indigo-500/20 text-indigo-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-300'
                }`}
            >
              <Bot className="w-3.5 h-3.5" />
              Server Data
              {selectedServer && (
                <span className={`text-[10px] font-normal ${activeTab === 'server' ? 'text-indigo-400' : 'text-slate-600'}`}>
                  — {selectedServer.name}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('acquisition')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'acquisition'
                ? 'bg-indigo-500/20 text-indigo-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-300'
                }`}
            >
              <Compass className="w-3.5 h-3.5" />
              Acquisition
            </button>
          </div>

          {/* ── ACQUISITION TAB ── */}
          {activeTab === 'acquisition' && <AcquisitionTab acquisition={selectedUser.acquisition} />}

          {/* ── PERSONAL USAGE TAB ── */}
          {activeTab === 'personal' && (
            <div className="space-y-5">

              {/* API Quota */}
              <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-sm font-black text-white">Personal API Quota</h3>
                    <p className="text-[10px] text-slate-500 mt-0.5">This user's own API token consumption — not tied to any server</p>
                  </div>
                  {!isEditingPlan && can('users_edit') && (
                    <button
                      onClick={() => {
                        setSelectedPlan(selectedUser.plan);
                        setIsEditingPlan(true);
                      }}
                      className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all cursor-pointer shrink-0 ${selectedUser.personalUsage?.plan === 'Pro'
                        ? 'bg-purple-500/10 border-purple-500/20 text-purple-300 hover:bg-purple-500/20'
                        : selectedUser.personalUsage?.plan === 'Premium'
                          ? 'bg-amber-500/10 border-amber-500/20 text-amber-300 hover:bg-amber-500/20'
                          : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-300 hover:bg-indigo-500/20'
                        }`}
                      title="Click to change plan"
                    >
                      {selectedUser.personalUsage?.plan} Plan
                    </button>
                  )}
                </div>

                {isEditingPlan && (
                  <div className="mb-5 p-4 bg-slate-900/50 border border-white/5 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Change Plan</p>
                    <div className="flex gap-2 mb-3">
                      {['Free', 'Premium', 'Pro'].map((plan) => (
                        <button
                          key={plan}
                          onClick={() => setSelectedPlan(plan)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all ${selectedPlan === plan
                            ? plan === 'Pro'
                              ? 'bg-purple-500/20 border-purple-500/50 text-purple-200'
                              : plan === 'Premium'
                                ? 'bg-amber-500/20 border-amber-500/50 text-amber-200'
                                : 'bg-indigo-500/20 border-indigo-500/50 text-indigo-200'
                            : 'bg-transparent border-white/5 text-slate-500 hover:border-white/10 hover:text-slate-400'
                            }`}
                        >
                          {plan}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          await changePlan(selectedUser.id, selectedPlan);
                          setIsEditingPlan(false);
                        }}
                        className="flex-1 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-bold transition-colors"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setIsEditingPlan(false)}
                        className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-400 rounded-lg text-xs font-bold transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-end justify-between mb-2">
                  <span className="text-3xl font-extrabold text-white font-mono">
                    {usageUsed.toLocaleString()}
                  </span>
                  <span className="text-sm text-slate-500 font-mono">/ {usageLimit.toLocaleString()}</span>
                </div>

                <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r transition-all duration-500 ${usageBarColor}`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <span>{usagePct.toFixed(1)}% consumed</span>
                  <span>{Math.max(0, usageLimit - usageUsed).toLocaleString()} remaining</span>
                </div>
              </div>




              {/* Recent API Activity */}
              {/* <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Recent API Activity
                </p>

                <div className="glass-panel rounded-2xl border-white/[0.05] overflow-hidden">
                  {selectedUser.usage?.activities?.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Type</th>
                          <th className="text-left px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Detail</th>
                          <th className="text-left px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">When</th>
                          <th className="text-right px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.usage.activities.map((act, i) => (
                          <tr
                            key={act.id}
                            className={`hover:bg-white/[0.02] transition-colors ${i < selectedUser.usage.activities.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
                          >
                            <td className="px-5 py-3.5 font-bold text-indigo-300 whitespace-nowrap">{act.type}</td>
                            <td className="px-5 py-3.5 text-slate-400">{act.detail}</td>
                            <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap hidden sm:table-cell">{act.date}</td>
                            <td className="px-5 py-3.5 text-right font-mono font-bold text-emerald-400 whitespace-nowrap">{act.cost}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-10 text-center text-xs text-slate-600">No API activity recorded.</div>
                  )}
                </div>
              </div> */}

            </div>
          )}

          {/* ── SERVER DATA TAB ── */}
          {activeTab === 'server' && (
            <div className="space-y-5">
              {selectedServer ? (
                <>
                  {/* Server identity + actions */}
                  <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 shrink-0">
                          <Bot className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">Currently Viewing Server</p>
                          <h2 className="text-lg font-black text-white">{selectedServer.name}</h2>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                            <span className="font-mono bg-slate-950 px-1.5 py-0.5 rounded border border-white/5">
                              ID: {selectedServer.id}
                            </span>
                            <span className="text-slate-600">·</span>
                            <span>Bot: ryzz-bot-v2</span>
                          </div>
                          {selectedServer.role && (
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]">
                              <span className="text-slate-500">{selectedUser.name} is</span>
                              <span className={`px-2 py-0.5 rounded-md border font-bold ${ROLE_STYLE[selectedServer.role]?.className || ROLE_STYLE.member.className}`}>
                                {ROLE_STYLE[selectedServer.role]?.long || selectedServer.role}
                              </span>
                              {selectedServer.paidByThisUser === true && (
                                <span className="px-2 py-0.5 rounded-md border font-bold border-lime-500/20 text-lime-300 bg-lime-500/5">
                                  and paid for the {selectedServer.plan} plan
                                </span>
                              )}
                              {selectedServer.paidByThisUser === false && selectedServer.buyerDiscordId && (
                                <span className="text-slate-500">
                                  · plan paid by{' '}
                                  <Link to={`/user/${selectedServer.buyerDiscordId}`} className="font-mono text-indigo-300 hover:text-indigo-200">
                                    {selectedServer.buyerDiscordId}
                                  </Link>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <span className={`px-3 py-1 text-[10px] tracking-wider uppercase font-extrabold rounded-lg border shrink-0 ${selectedServer.status === 'Online'
                        ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                        : selectedServer.status === 'Maintenance'
                          ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                          : 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                        }`}>
                        {selectedServer.status}
                      </span>
                    </div>
                    {/* 
                    <div className="grid grid-cols-3 gap-2.5 mt-5 pt-5 border-t border-white/5">
                      <button
                        onClick={() => toggleServerStatus(selectedUser.id, selectedServer.id)}
                        className="py-2.5 px-3 bg-slate-900/60 hover:bg-indigo-500/10 border border-white/5 hover:border-indigo-500/30 rounded-xl text-xs font-bold text-slate-300 hover:text-indigo-200 transition-all active:scale-95 flex items-center justify-center gap-2"
                      >
                        <Server className="w-3.5 h-3.5 text-indigo-400" />
                        Toggle Bot
                      </button>
                      <button
                        onClick={() => rebootServer(selectedUser.id, selectedServer.id)}
                        className="py-2.5 px-3 bg-slate-900/60 hover:bg-indigo-500/10 border border-white/5 hover:border-indigo-500/30 rounded-xl text-xs font-bold text-slate-300 hover:text-indigo-200 transition-all active:scale-95 flex items-center justify-center gap-2"
                      >
                        <Bot className="w-3.5 h-3.5 text-indigo-400" />
                        Sync & Reboot
                      </button>
                      <button
                        onClick={() => processPendingCharacters(selectedUser.id, selectedServer.id)}
                        disabled={!selectedServer.charactersPending}
                        className={`py-2.5 px-3 border rounded-xl text-xs font-bold transition-all active:scale-95 flex items-center justify-center gap-2 ${selectedServer.charactersPending > 0
                          ? 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
                          : 'bg-slate-900/30 border-white/5 text-slate-500 cursor-not-allowed'
                        }`}
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Process Queue ({selectedServer.charactersPending || 0})
                      </button>
                    </div> */}
                  </div>

                  {/* Which link installed the bot here */}
                  <InstalledViaCard installedVia={selectedServer.installedVia} />

                  {/* Character Usage */}
                  <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-sm font-black text-white">Character Usage</h3>
                        <p className="text-[10px] text-slate-500 mt-0.5">Monthly translation quota for this server only</p>
                      </div>
                      {/* <button
                        onClick={() => resetCharacters(selectedUser.id, selectedServer.id)}
                        className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-slate-200 text-xs font-bold rounded-xl transition-all border border-white/5 hover:border-white/10 active:scale-95 shrink-0"
                      >
                        Reset for Free!
                      </button> */}
                    </div>

                    <div className="flex items-end justify-between mb-2">
                      <span className="text-3xl font-extrabold text-white font-mono">
                        {(selectedServer.charactersUsed || 0).toLocaleString('de-DE')}
                      </span>
                      <span className="text-sm text-slate-500 font-mono">
                        / {(selectedServer.maxCharactersPerDay || 0).toLocaleString('de-DE')}
                      </span>
                    </div>

                    <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden mb-3">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r transition-all duration-500 ${serverUsagePct > 85
                          ? 'from-rose-500 to-red-400 shadow-[0_0_8px_rgba(239,68,68,0.4)]'
                          : serverUsagePct > 60
                            ? 'from-amber-500 to-orange-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]'
                            : 'from-emerald-500 to-teal-400 shadow-[0_0_8px_rgba(16,185,129,0.4)]'
                          }`}
                        style={{ width: `${serverUsagePct}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <span>Resets in 1 day</span>
                      <span className="text-indigo-400 border border-indigo-500/20 bg-indigo-500/5 px-2 py-0.5 rounded">
                        {selectedServer.plan ? selectedServer.plan.charAt(0).toUpperCase() + selectedServer.plan.slice(1) : "Free"} Plan
                      </span>
                    </div>
                  </div>

                  {/* Server Subscription & Billing */}
                  <div className="glass-panel rounded-2xl p-5 border-white/[0.05] relative overflow-hidden bg-gradient-to-br from-indigo-950/10 via-[#070a13] to-slate-950/30">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />

                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-sm font-black text-white flex items-center gap-1.5">
                          <Layers className="w-4 h-4 text-indigo-400" />
                          Server Subscription & Billing
                        </h3>
                        <p className="text-[10px] text-slate-500 mt-0.5">Subscription level, recurring costs, and total budget consumed on this server</p>
                      </div>
                      {selectedServer.subscriptionStatus !== null ? (
                        <span className={`px-3 py-1.5 text-xs font-bold rounded-xl border shrink-0 cursor-not-allowed opacity-60 ${selectedServer.plan === 'pro'
                          ? 'bg-purple-500/10 border-purple-500/20 text-purple-300'
                          : selectedServer.plan === 'premium'
                            ? 'bg-amber-500/10 border-amber-500/20 text-amber-300'
                            : 'bg-slate-500/10 border-white/5 text-slate-400'
                          }`} title="Managed by Stripe — cannot be changed by admin">
                          {selectedServer.plan ? selectedServer.plan.charAt(0).toUpperCase() + selectedServer.plan.slice(1) : 'Free'} Plan
                        </span>
                      ) : !isEditingServerPlan && can('users_edit') && (
                        <button
                          onClick={() => {
                            setSelectedServerPlan(selectedServer.plan || 'free');
                            setIsEditingServerPlan(true);
                          }}
                          className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all cursor-pointer shrink-0 ${selectedServer.plan === 'pro'
                            ? 'bg-purple-500/10 border-purple-500/20 text-purple-300 hover:bg-purple-500/20'
                            : selectedServer.plan === 'premium'
                              ? 'bg-amber-500/10 border-amber-500/20 text-amber-300 hover:bg-amber-500/20'
                              : 'bg-slate-500/10 border-white/5 text-slate-400 hover:bg-slate-500/20'
                            }`}
                          title="Click to change server plan"
                        >
                          {selectedServer.plan ? selectedServer.plan.charAt(0).toUpperCase() + selectedServer.plan.slice(1) : 'Free'} Plan
                        </button>
                      )}
                    </div>

                    {isEditingServerPlan && (
                      <div className="mb-5 p-4 bg-slate-900/50 border border-white/5 rounded-xl">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Change Server Plan</p>
                        <div className="flex gap-2 mb-3">
                          {['free', 'premium', 'pro'].map((plan) => (
                            <button
                              key={plan}
                              onClick={() => setSelectedServerPlan(plan)}
                              className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all capitalize ${selectedServerPlan === plan
                                ? plan === 'pro'
                                  ? 'bg-purple-500/20 border-purple-500/50 text-purple-200'
                                  : plan === 'premium'
                                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-200'
                                    : 'bg-slate-500/20 border-white/20 text-slate-300'
                                : 'bg-transparent border-white/5 text-slate-500 hover:border-white/10 hover:text-slate-400'
                                }`}
                            >
                              {plan.charAt(0).toUpperCase() + plan.slice(1)}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              await changeServerPlan(selectedUser.id, selectedServer.id, selectedServerPlan);
                              setIsEditingServerPlan(false);
                            }}
                            className="flex-1 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-bold transition-colors"
                          >
                            Save Changes
                          </button>
                          <button
                            onClick={() => setIsEditingServerPlan(false)}
                            className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-400 rounded-lg text-xs font-bold transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {(selectedServer.plan === 'free' || !selectedServer.plan) ? null : selectedServer.subscriptionStatus === null ? (
                      /* ── Admin Granted (subscriptionStatus: null) ── */
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                        <div className="p-3.5 bg-slate-950/50 border border-white/5 rounded-xl">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Current Active Plan</span>
                          <span className="text-sm font-black text-white mt-2 flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${selectedServer.plan === 'free' || !selectedServer.plan ? 'bg-slate-500' : 'bg-emerald-400'}`} />
                            {selectedServer.plan === 'pro' ? 'Pro Plan' : selectedServer.plan === 'premium' ? 'Premium Plan' : 'Free Tier'}
                          </span>
                        </div>

                        <div className="sm:col-span-2 p-3.5 bg-indigo-500/10 border border-indigo-500/30 rounded-xl flex items-center gap-3.5">
                          <div className="p-2.5 bg-indigo-500/20 rounded-xl shrink-0">
                            <Shield className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div>
                            <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider block mb-0.5">Access Method</span>
                            <span className="text-xs font-bold text-white block">Admin Granted Access</span>
                            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                              This plan has been manually assigned by an administrator. Only access is granted, and no recurring billing details apply.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* ── Active Subscription ── */
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-2">
                        <div className="p-3.5 bg-slate-950/50 border border-white/5 rounded-xl">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Current Active Plan</span>
                          <span className="text-sm font-black text-white mt-2 flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${selectedServer.plan === 'free' || !selectedServer.plan ? 'bg-slate-500' : 'bg-emerald-400'}`} />
                            {selectedServer.plan === 'pro' ? 'Pro Plan' : selectedServer.plan === 'premium' ? 'Premium Plan' : 'Free Tier'}
                          </span>
                        </div>

                        <div className="p-3.5 bg-slate-950/50 border border-white/5 rounded-xl">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Monthly Cost</span>
                          <span className="text-lg font-black text-white font-mono mt-1 block">
                            ${(selectedServer.subscriptionCost || 0).toFixed(2)}
                            <span className="text-[10px] font-normal text-slate-500 font-sans">/mo</span>
                          </span>
                        </div>

                        <div className="p-3.5 bg-slate-950/50 border border-white/5 rounded-xl">
                          <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider block">Next Billing Cycle</span>
                          <span className="text-xs font-bold text-slate-300 mt-2.5 block">
                            {selectedServer.subscriptionStatus === 'canceled' ? (
                              <span className="text-slate-500">None (Canceled)</span>
                            ) : selectedServer.subscriptionCurrentPeriodEnd ? (
                              new Date(selectedServer.subscriptionCurrentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            ) : (
                              <span className="text-slate-500">N/A</span>
                            )}
                          </span>
                        </div>

                        <div className="p-3.5 bg-slate-950/50 border border-white/5 rounded-xl">
                          <span className="text-[9px] font-bold text-rose-400 uppercase tracking-wider block">Expiry Date</span>
                          <span className="text-xs font-bold mt-2.5 block">
                            {selectedServer.subscriptionCurrentPeriodEnd ? (
                              <span className="text-rose-400 font-black">
                                {new Date(selectedServer.subscriptionCurrentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            ) : (
                              <span className="text-slate-500">N/A</span>
                            )}
                          </span>
                        </div>

                        <div className="p-3.5 bg-slate-950/50 border border-white/5 rounded-xl">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Total Spent</span>
                          <span className="text-lg font-black text-emerald-400 font-mono mt-1 block">
                            ${(typeof selectedServer.subscriptionSpent === 'number' ? selectedServer.subscriptionSpent : (selectedServer.subscriptionCost || 0)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Server Analytics Chart */}
                  <AnalyticsChart userId={selectedUser.id} serverId={selectedServer.id} />

                  {/* Server Stats */}
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">
                      Server Statistics — {selectedServer.name}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { icon: <Users className="w-4 h-4 text-emerald-400" />, label: 'Total Members', value: (selectedServer.members || 0).toLocaleString(), sub: 'Registered accounts' },
                        { icon: <Hash className="w-4 h-4 text-indigo-400" />, label: 'Channels', value: selectedServer.channels || 0, sub: 'Text & voice channels' },
                        { icon: <Layers className="w-4 h-4 text-purple-400" />, label: 'Categories', value: selectedServer.categories || 0, sub: 'Channel categories' },
                        { icon: <FileText className="w-4 h-4 text-cyan-400" />, label: 'Translated Chars', value: Math.floor((selectedServer.charactersUsed || 0) * 0.95).toLocaleString(), sub: 'Net parsed characters' },
                        { icon: <MessageSquare className="w-4 h-4 text-amber-400" />, label: 'Translated Msgs', value: Math.floor((selectedServer.charactersUsed || 0) / 12).toLocaleString(), sub: 'Messages processed' },
                        { icon: <Zap className="w-4 h-4 text-rose-400" />, label: 'Avg Latency', value: selectedServer.status === 'Online' ? '42ms' : '0ms', sub: 'Gateway roundtrip' },
                      ].map(({ icon, label, value, sub }) => (
                        <div key={label} className="p-4 bg-slate-900/40 border border-white/5 hover:border-indigo-500/20 rounded-xl transition-all space-y-2">
                          <div className="flex items-center gap-2">
                            {icon}
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
                          </div>
                          <span className="text-xl font-bold text-white font-mono block">{value}</span>
                          <span className="text-[9px] text-slate-500 block">{sub}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Event Logs */}
                  {/* <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">
                      Event Logs — {selectedServer.name}
                    </p>
                    <div className="bg-[#04060d] border border-white/5 rounded-xl p-4 font-mono text-[11px] text-slate-400 h-40 overflow-y-auto space-y-1.5 shadow-inner">
                      {selectedServer.logs?.length > 0 ? (
                        selectedServer.logs.map((log, lIdx) => {
                          let color = 'text-slate-400';
                          if (log.includes('[ERROR]') || log.includes('[CRITICAL]')) color = 'text-rose-400';
                          else if (log.includes('[SUCCESS]') || log.includes('[OK]') || log.includes('[ONLINE]')) color = 'text-emerald-400';
                          else if (log.includes('[WARN]')) color = 'text-amber-400';
                          else if (log.includes('[ADMIN]')) color = 'text-indigo-400 font-semibold';
                          return <div key={lIdx} className={color}>{log}</div>;
                        })
                      ) : (
                        <div className="text-slate-600 text-center py-10">No logs for this server.</div>
                      )}
                    </div>
                  </div> */}
                </>
              ) : (
                <div className="glass-panel rounded-2xl p-12 border-white/[0.05] text-center flex flex-col items-center justify-center min-h-[400px]">
                  <Bot className="w-12 h-12 text-slate-600 animate-pulse mb-3" />
                  <h3 className="text-sm font-bold text-slate-300">No server selected</h3>
                  <p className="text-xs text-slate-500 mt-1">Select a server from the left panel to view its data.</p>
                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
